import React, { useId, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import { Button, Checkbox, DatePicker, Input, Label } from '@column/ui';

import { BUSINESS_LEGAL_TYPE_OPTIONS } from '../_util/entityOptions';
import {
  BusinessEntityFormValues,
  IdentificationDocument,
  createDefaultAssociatedPerson,
} from '../_util/useEntityForm';
import { RenderFields, Section } from '~/components';
import { SectionWithFields } from '~/components/RenderFields';
import { useAddress } from '~/hooks';
import { getDateFormat } from '~/util';

import { AssociatedPersonFields } from './AssociatedPersonFields';
import {
  CountrySelect,
  EntityAddressFields,
  OptionSelect,
  PaymentRailsInput,
  SensitiveInput,
  SplitRow,
  StringListInput,
  YesNoInput,
  parseIsoDate,
} from './EntityFieldInputs';

const INCORPORATION_YEAR_OPTIONS = Array.from({ length: 250 }, (_, index) => {
  const year = String(new Date().getFullYear() - index);
  return { label: year, value: year };
});

// Shown inside the EIN field while it's empty during entity creation; an EIN and a pending EIN can't coexist.
const EinPendingToggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ checked, disabled, onCheckedChange }) => {
  const pendingId = useId();
  return (
    <>
      <Label htmlFor={pendingId} className="cursor-pointer text-sm font-medium text-gray-500 select-none">
        Pending
      </Label>
      <Checkbox
        id={pendingId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange(next === true)}
      />
    </>
  );
};

export const BusinessEntity: React.FC<{
  id?: string;
  disableForm?: boolean;
  isRoot?: boolean;
}> = (props) => {
  const form = useFormContext();
  const { countries, states } = useAddress({ types: ['state', 'country'] });
  const associatedPersonRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'associatedPersons',
  });

  const isEinPending = form.watch('einPending') === true;
  // Watched so the State of Incorporation options refilter when the country changes.
  const incorporationCountryCode = form.watch('countryOfIncorporation') as string | undefined;

  const countryOptions = countries.map((c) => ({
    label: c.name,
    value: c.code,
  }));

  const informationSections: SectionWithFields<BusinessEntityFormValues, undefined>[] = [
    {
      headline: 'Business Information',
      fields: [
        {
          id: 'businessName',
          label: 'Business Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur }, { error }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Business Name"
              disabled={props.disableForm}
              aria-invalid={!!error}
            />
          ),
        },
        {
          id: 'ein',
          label: 'Employer Identification Number',
          children: ({ value, onChange, onBlur }, { error }) => {
            // Only the create flow has a "pending" EIN; elsewhere it's a plain masked field.
            const showEinPending = !props.isRoot && !props.id && (!props.disableForm || isEinPending);

            return (
              <SensitiveInput
                value={(value as string) ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="EIN"
                label="Employer Identification Number"
                defaultVisible={!props.id}
                isDisabled={props.disableForm || (showEinPending && isEinPending)}
                hasError={!!error}
                emptyAdornment={
                  showEinPending && (
                    <EinPendingToggle
                      checked={isEinPending}
                      disabled={props.disableForm}
                      onCheckedChange={(checked) => {
                        if (props.disableForm) return;

                        form.setValue('einPending', checked, { shouldDirty: true, shouldValidate: true });
                        if (checked) {
                          form.setValue('ein', '', { shouldDirty: true, shouldValidate: true });
                        }
                      }}
                    />
                  )
                }
              />
            );
          },
        },
        {
          id: 'website',
          label: 'Website',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Website"
              disabled={props.disableForm}
            />
          ),
        },
      ],
    },
  ];

  const detailSections: SectionWithFields<BusinessEntityFormValues, undefined>[] = [
    {
      headline: 'Business Details',
      isCollapsed: true,
      fields: [
        {
          id: 'legalType',
          label: 'Legal Type',
          children: ({ value, onChange }) => (
            <OptionSelect
              value={value as string | undefined}
              onChange={onChange}
              options={BUSINESS_LEGAL_TYPE_OPTIONS}
              placeholder="Select legal type..."
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'industry',
          label: 'Industry',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Industry"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'dbaName',
          label: 'Doing Business As (DBA)',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="DBA name, if different from the legal name"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'registrationId',
          label: 'Registration ID',
          description: 'International business registration identifier. EIN or Registration ID is required.',
          children: ({ value, onBlur }) => (
            <SplitRow>
              <Input
                className="w-full"
                value={(value as IdentificationDocument)?.number ?? ''}
                onChange={(event) => form.setValue('registrationId.number', event.target.value, { shouldDirty: true })}
                onBlur={onBlur}
                placeholder="Registration Number"
                disabled={props.disableForm}
              />
              <CountrySelect
                value={(value as IdentificationDocument)?.countryCode}
                onChange={(next) => form.setValue('registrationId.countryCode', next, { shouldDirty: true })}
                options={countryOptions}
                placeholder="Issuing country..."
                isDisabled={props.disableForm}
              />
            </SplitRow>
          ),
        },
      ],
    },
    {
      headline: 'Incorporation',
      isCollapsed: true,
      fields: [
        {
          id: 'countryOfIncorporation',
          label: 'Country of Incorporation',
          children: ({ value, onChange }) => (
            <CountrySelect
              value={value as string | undefined}
              onChange={onChange}
              options={countryOptions}
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'stateOfIncorporation',
          label: 'State of Incorporation',
          children: ({ value, onChange }) => {
            // Only build the state list once a country is chosen; otherwise it materializes every
            // region worldwide (~5k options), which made expanding this section slow.
            const stateOptions = incorporationCountryCode
              ? states
                  .filter((s) => s.country_code === incorporationCountryCode)
                  .map((s) => ({ label: `${s.name} (${s.state_code})`, value: s.state_code }))
              : [];
            return (
              <OptionSelect
                value={value as string | undefined}
                onChange={onChange}
                options={stateOptions}
                placeholder={incorporationCountryCode ? 'Select state...' : 'Select a country first...'}
                isDisabled={props.disableForm || !incorporationCountryCode}
              />
            );
          },
        },
        {
          id: 'dateOfIncorporation',
          label: 'Date of Incorporation',
          children: ({ value, onChange, onBlur }, { error }) => (
            <DatePicker
              value={parseIsoDate(value as string | undefined)}
              onValueChange={(next) => onChange(next ? getDateFormat(next) : '')}
              onBlur={onBlur}
              disabledDays={{ after: new Date() }}
              editable
              clearable
              disabled={props.disableForm}
              aria-invalid={!!error}
            />
          ),
        },
        {
          id: 'yearOfIncorporation',
          label: 'Year of Incorporation',
          children: ({ value, onChange }) => {
            const year = (value as string | undefined) ?? '';
            // Keep a stored year outside the list selectable, so it still shows.
            const options =
              year && !INCORPORATION_YEAR_OPTIONS.some((option) => option.value === year)
                ? [{ label: year, value: year }, ...INCORPORATION_YEAR_OPTIONS]
                : INCORPORATION_YEAR_OPTIONS;
            return (
              <OptionSelect
                value={year}
                onChange={onChange}
                options={options}
                placeholder="Select year..."
                isDisabled={props.disableForm}
              />
            );
          },
        },
      ],
    },
    {
      headline: 'Operations & Activity',
      isCollapsed: true,
      fields: [
        {
          id: 'description',
          label: 'Description',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Description of the business entity"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'purposeOfAccount',
          label: 'Purpose of Account',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Intended use of the account"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'accountUsage',
          label: 'Account Usage',
          description: 'Comma-separated list.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="Possible uses of the accounts"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'paymentVolumes',
          label: 'Payment Volumes',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Expected payment volumes"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'countriesOfOperation',
          label: 'Countries of Operation',
          description: 'Comma-separated ISO country codes, e.g. US, CA, GB.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="US, CA, GB"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'sourceOfFunds',
          label: 'Source of Funds',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Primary source of funds"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'expectedUseOfFunds',
          label: 'Expected Use of Funds',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Expected use of funds"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'endCustomerType',
          label: 'End Customer Type',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Type of end customer the business serves"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'expectedPaymentRails',
          label: 'Expected Payment Rails',
          children: ({ value, onChange }) => (
            <PaymentRailsInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
        {
          id: 'makesInternationalPayments',
          label: 'Makes International Payments?',
          children: ({ value, onChange }) => (
            <YesNoInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
        {
          id: 'hasVirtualAddress',
          label: 'Has Virtual Address?',
          children: ({ value, onChange }) => (
            <YesNoInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
      ],
    },
    {
      headline: 'Compliance',
      isCollapsed: true,
      fields: [
        {
          id: 'isHighRisk',
          label: 'High Risk?',
          children: ({ value, onChange }) => (
            <YesNoInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
      ],
    },
  ];

  return (
    <>
      <RenderFields sections={informationSections} />
      <EntityAddressFields isDisabled={props.disableForm} />
      <RenderFields sections={detailSections} />
      <Section headline="Associated Persons">
        <div className="flex flex-col gap-4">
          {(fields ?? []).map((field, index) => (
            <div
              key={field.id}
              ref={(el) => (associatedPersonRefs.current[index] = el)}
              className="rounded-lg bg-gray-25 shadow-[0_0_0_1px_var(--color-gray-150)] dark:bg-white/[0.02] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]"
            >
              <div className="flex items-center gap-6 py-4 pr-4 pl-(--page-margin)">
                <h3 className="m-0 flex flex-1 items-center gap-2 text-base leading-5 font-medium tracking-[-0.02em] text-foreground">
                  <span className="flex size-6 items-center justify-center rounded-full bg-background text-sm font-normal text-muted-foreground shadow-[0_0_0_1px_var(--color-gray-150)] tabular-nums dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
                    {index + 1}
                  </span>
                  Associated Person
                </h3>
                {fields.length > 1 && !props.disableForm && (
                  <Button variant="danger" size="sm" onClick={() => remove(index)}>
                    Remove
                  </Button>
                )}
              </div>
              <div className="pb-4">
                <AssociatedPersonFields index={index} disabled={props.disableForm} />
              </div>
            </div>
          ))}
        </div>
        {!props.disableForm && (
          <Button
            type="button"
            onClick={() => {
              append(createDefaultAssociatedPerson());
              setTimeout(() => {
                associatedPersonRefs.current[fields.length]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 0);
            }}
            size="sm"
            variant="muted"
            className="w-full bg-gray-50 hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10"
          >
            <Icon.Plus className="[--icon-size:16px]" />
            Add Associated Person
          </Button>
        )}
      </Section>
    </>
  );
};
